import { Box, Typography } from '@mui/material';
import { WidgetWrapper } from '../WidgetWrapper';
import { cardPumpStatus, cardPumpImage, cardPumpMode } from '../style';
import { pump, StyledCardPumpStatus, modeValueStyle } from './style';
import { AlarmKind } from '../../types';

import { CRANE_PUMP, PUMP_1, PUMP_2, PUMP_3 } from 'src/features/sites';
import { PumpAsset } from 'src/types/assets';

import { PropelerPumpIcon } from '../PropelerPumpIcon';
import { useGetMultipleLatestReadings } from 'src/hooks/apiHooks/useGetMultipleLatestReadings';
import { useMemo } from 'react';
import { CranePumpWidget } from 'src/assets/widgets/CranePumpWidget';
import { DosingPumpIcon } from 'src/assets/icons/DosingPumpIcon';
import { PumpIcon } from 'src/assets/icons/PumpIcon';
import { usePumpStateAndMode } from 'src/hooks/featureHooks/usePumpStateAndMode';

type Props = {
  title: string;
  runMode: boolean | null;
  alarm?: AlarmKind;
  isEditMode?: boolean;
  widgetId?: string;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  widgetSubType?: string;
  asset: PumpAsset;
  assetActiveAlarm: number | null;
};

export const Pump = ({
  title,
  runMode,
  isEditMode,
  widgetId,
  toggleAddOrRemoveHandler,
  testId,
  widgetSubType,
  asset,
  assetActiveAlarm,
}: Props) => {
  const renderWidgetIcon = (isRunMode: boolean, isAlarmActive: boolean) => {
    const iconWidth = 70;
    const iconHeight = 70;
    if (widgetSubType === PUMP_1) return <PumpIcon width={90} height={90} />;
    if (widgetSubType === PUMP_2) return <DosingPumpIcon width={iconWidth} height={iconHeight} />;
    if (widgetSubType === PUMP_3)
      return (
        <PropelerPumpIcon isRunMode={isRunMode} isAlarmActive={isAlarmActive} showRotator={true} />
      );
    if (widgetSubType === CRANE_PUMP) return <CranePumpWidget width={50} height={90} />;
    return <PumpIcon />;
  };

  const { activeState, isRunning, activeMode } = usePumpStateAndMode({ asset: asset });

  const { readingsMap: multipleReadings, isDataReady: isMultipleDataReady } =
    useGetMultipleLatestReadings({
      items: asset?.assignedAlarmTags,
    });

  const values = Object.values(multipleReadings)?.map((tagData) => {
    const tagId = Object.keys(tagData)[0];
    const values = tagData[tagId]?.values ?? {};
    return Object.values(values)[0];
  });
  const isAlarmActive: boolean =
    assetActiveAlarm !== null ? assetActiveAlarm === 1 : values.some((item) => Number(item) > 0);

  let modeValue; // Use mode directly from custom hook
  if (runMode !== null) {
    modeValue = runMode;
  } else if (runMode === null) {
    modeValue = activeMode;
  }

  const isAlarmReady = useMemo(
    () =>
      isMultipleDataReady &&
      (isAlarmActive !== undefined || isAlarmActive === false || isAlarmActive),
    [isMultipleDataReady, isAlarmActive]
  );

  const isReady = isMultipleDataReady && isAlarmReady;

  const isPumpMappedToAlarmTags = asset?.assignedAlarmTags?.some((tag) => tag.assignedAlarmTagId);

  return (
    <WidgetWrapper
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={isReady}
      isPumpWidget={true}
      isAlarmForPumpActive={isAlarmActive}
      isPumpMappedToAlarmTags={isPumpMappedToAlarmTags}
    >
      <Box sx={pump}>
        <Box sx={cardPumpImage}>{renderWidgetIcon(Boolean(isRunning), isAlarmActive)}</Box>
        <Box sx={cardPumpStatus}>
          <StyledCardPumpStatus isRunMode={Boolean(isRunning)} variant="contained">
            {activeState}
          </StyledCardPumpStatus>
        </Box>

        <Box sx={cardPumpMode}>
          {modeValue ? (
            <Box>
              <Typography>Mode: </Typography>
              <Typography sx={modeValueStyle}>{modeValue}</Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    </WidgetWrapper>
  );
};
